import type { ProtocolData } from "@solomon-apps/shared";
import { API_URLS, fetchJson } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import React from "react";

/**
 * Fetch protocol data from Solomon Data API
 */
async function fetchProtocolData(signal?: AbortSignal): Promise<ProtocolData> {
    const url = `${API_URLS.SOLOMON_DATA}/api/solomon-protocol/protocol-data`;
    const data = await fetchJson<ProtocolData>(url, signal);

    // Simple validation - just ensure we have an object
    if (!data || typeof data !== "object") {
        console.warn("Invalid protocol data received:", data);
        throw new Error("Invalid protocol data received from API");
    }

    return data;
}

/**
 * Hook to fetch comprehensive protocol data
 *
 * Returns protocol TVL, staked TVL, positions, custody holdings, vault holdings,
 * yield distributor holdings, and various APY metrics.
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useProtocolData();
 * if (data) {
 *   console.log(`Protocol TVL: ${data.protocolTvl}`);
 *   console.log(`Staked TVL: ${data.stakedTvl}`);
 * }
 * ```
 */
export function useProtocolData(): UseQueryResult<ProtocolData> {
    return useQuery<ProtocolData>({
        queryKey: ["protocol-data"],
        queryFn: ({ signal }) => fetchProtocolData(signal),
        staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
        refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
        retry: 3, // Retry 3 times on failure
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
    });
}

/**
 * Extracted distributions data from protocol data
 */
export interface DistributionsData {
    /** Total yield distributed (USD string) */
    total: string;
    /** Current daily realized collateral yield APY */
    currentApy: string;
}

/**
 * Hook to fetch distribution data from protocol data
 *
 * This is a convenience wrapper around useProtocolData that extracts
 * only the distribution-related fields.
 *
 * @example
 * ```tsx
 * const { distributions, isLoading, error } = useDistributionsData();
 * if (distributions) {
 *   console.log(`Total distributed: ${distributions.total}`);
 * }
 * ```
 */
export function useDistributionsData(): {
    distributions: DistributionsData | null;
} & UseQueryResult<ProtocolData> {
    const query = useProtocolData();

    const distributions = React.useMemo((): DistributionsData | null => {
        const d = query.data;
        if (!d) return null;

        return {
            total: d.totalYieldDistributed || "0",
            currentApy: d.dailyRealizedCollateralYieldApy || "0"
        };
    }, [query.data]);

    return { distributions, ...query };
}
